/**
 * Timezone utility functions for consistent date/time handling
 * All dates are stored in UTC and converted to local time for display
 */

import { format, parseISO, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

/**
 * Get the user's current timezone (IANA format)
 * @returns {string} e.g., "America/New_York"
 */
export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get timezone abbreviation for display
 * @returns {string} e.g., "EST", "PST", "UTC"
 */
export function getTimezoneAbbreviation() {
  const date = new Date();
  const timeString = date.toLocaleTimeString("en-US", { timeZoneName: "short" });
  const match = timeString.match(/\s([A-Z]{2,5})$/);
  return match ? match[1] : getUserTimezone();
}

/**
 * Get the timezone offset in hours from UTC
 * @returns {number} e.g., -5 for EST
 */
export function getTimezoneOffset() {
  return -new Date().getTimezoneOffset() / 60;
}

/**
 * Parse an ISO date string and return a Date object
 * @param {string} isoString - ISO 8601 date string
 * @returns {Date|null}
 */
export function parseDate(isoString) {
  if (!isoString) return null;
  const date = parseISO(isoString);
  return isValid(date) ? date : null;
}

/**
 * Format a date for display in user's local timezone
 * @param {string|Date} dateInput - ISO string or Date object
 * @param {string} formatStr - date-fns format string (default: "MMM d, yyyy")
 * @returns {string}
 */
export function formatDate(dateInput, formatStr = "MMM d, yyyy") {
  const date = typeof dateInput === "string" ? parseDate(dateInput) : dateInput;
  if (!date || !isValid(date)) return "";
  return format(date, formatStr);
}

/**
 * Format a date with time in user's local timezone
 * @param {string|Date} dateInput - ISO string or Date object
 * @param {string} formatStr - date-fns format string (default: "MMM d, yyyy h:mm a")
 * @returns {string}
 */
export function formatDateTime(dateInput, formatStr = "MMM d, yyyy h:mm a") {
  const date = typeof dateInput === "string" ? parseDate(dateInput) : dateInput;
  if (!date || !isValid(date)) return "";
  return format(date, formatStr);
}

/**
 * Format time only in user's local timezone
 * @param {string|Date} dateInput - ISO string or Date object
 * @param {string} formatStr - date-fns format string (default: "h:mm a")
 * @returns {string}
 */
export function formatTime(dateInput, formatStr = "h:mm a") {
  const date = typeof dateInput === "string" ? parseDate(dateInput) : dateInput;
  if (!date || !isValid(date)) return "";
  return format(date, formatStr);
}

/**
 * Format a date with timezone indicator
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} e.g., "Jan 15, 2024 2:30 PM EST"
 */
export function formatDateTimeWithTz(dateInput) {
  const date = typeof dateInput === "string" ? parseDate(dateInput) : dateInput;
  if (!date || !isValid(date)) return "";
  const tz = getUserTimezone();
  return formatInTimeZone(date, tz, "MMM d, yyyy h:mm a zzz");
}

/**
 * Convert UTC hour (0-23) to local hour
 * @param {number} utcHour - Hour in UTC (0-23)
 * @returns {number} Hour in local timezone (0-23)
 */
export function utcHourToLocal(utcHour) {
  const now = new Date();
  const utcDate = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    utcHour,
    0,
    0
  ));
  return utcDate.getHours();
}

/**
 * Convert local hour to UTC hour
 * @param {number} localHour - Hour in local timezone (0-23)
 * @returns {number} Hour in UTC (0-23)
 */
export function localHourToUtc(localHour) {
  const now = new Date();
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    localHour,
    0,
    0
  );
  return localDate.getUTCHours();
}

/**
 * Remap hour data from UTC to local timezone
 * @param {Object} byHourUTC - Object with UTC hour keys (0-23) and count values
 * @returns {Object} Object with local hour keys (0-23) and count values
 */
export function remapHoursToLocal(byHourUTC) {
  const localHours = {};
  for (let utcHour = 0; utcHour < 24; utcHour++) {
    const localHour = utcHourToLocal(utcHour);
    const count = byHourUTC[utcHour] || 0;
    localHours[localHour] = (localHours[localHour] || 0) + count;
  }
  return localHours;
}

/**
 * Format hour for display (e.g., "2 PM", "12 AM")
 * @param {number} hour - Hour (0-23)
 * @returns {string}
 */
export function formatHour(hour) {
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour} ${period}`;
}

/**
 * Convert local date and time inputs to UTC ISO string
 * Used when user enters a date/time in their local timezone
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM format (24-hour)
 * @returns {string} ISO 8601 UTC string
 */
export function localInputToUTC(dateStr, timeStr = "12:00") {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = timeStr.split(":").map(Number);
  
  // Create a local date (browser interprets this as local time)
  const localDate = new Date(year, month - 1, day, hours, minutes, 0);
  
  if (!isValid(localDate)) return null;
  return localDate.toISOString();
}

/**
 * Convert UTC ISO string to local date and time for form inputs
 * @param {string} isoString - ISO 8601 UTC string
 * @returns {{date: string, time: string}} Object with date (YYYY-MM-DD) and time (HH:MM)
 */
export function utcToLocalInput(isoString) {
  if (!isoString) return { date: "", time: "12:00" };
  
  const date = parseDate(isoString);
  if (!date) return { date: "", time: "12:00" };
  
  // Format in local timezone for input values
  const dateStr = format(date, "yyyy-MM-dd");
  const timeStr = format(date, "HH:mm");
  
  return { date: dateStr, time: timeStr };
}

/**
 * Check if a date is in the past (in user's local timezone)
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {boolean}
 */
export function isPast(dateInput) {
  const date = typeof dateInput === "string" ? parseDate(dateInput) : dateInput;
  if (!date) return false;
  return date < new Date();
}

/**
 * Check if a date is in the future (in user's local timezone)
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {boolean}
 */
export function isFuture(dateInput) {
  const date = typeof dateInput === "string" ? parseDate(dateInput) : dateInput;
  if (!date) return false;
  return date > new Date();
}

/**
 * Get relative time string (e.g., "2 days ago", "in 3 hours")
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string}
 */
export function getRelativeTime(dateInput) {
  const date = typeof dateInput === "string" ? parseDate(dateInput) : dateInput;
  if (!date || !isValid(date)) return "";
  
  const now = new Date();
  const diffMs = date - now;
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);
  
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  
  if (Math.abs(diffDay) >= 1) {
    return rtf.format(diffDay, "day");
  } else if (Math.abs(diffHour) >= 1) {
    return rtf.format(diffHour, "hour");
  } else if (Math.abs(diffMin) >= 1) {
    return rtf.format(diffMin, "minute");
  } else {
    return rtf.format(diffSec, "second");
  }
}

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 * @returns {string}
 */
export function getTodayDateString() {
  return format(new Date(), "yyyy-MM-dd");
}

/**
 * Get minimum datetime string for input (current time in local timezone)
 * @returns {string} datetime-local format: YYYY-MM-DDTHH:MM
 */
export function getMinDateTimeString() {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm");
}
